import { useQueries, useQuery } from "react-query";
import { AxiosError } from "axios";
import _orderBy from "lodash/orderBy";
import { authAxios as axios } from "api-generate-types/src/axios";
import { useErrorBoundary } from "component-library";
import { TranscodeStatus } from "shared-types";
import { HALManager } from "core";
import UserManager from "app/core/user-manager";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { MediaMetadata, OutputGroupType } from "mediaModules/media/models";
import { getApiUrl, getTitaniumApiUrl } from "services/UrlBuilder";
import { MediaItem } from "pages/Profile/types";

const GET_MEDIA_QUERY_KEY = "GET_MEDIA_QUERY_KEY";
const GET_MEDIA_OBJECT_QUERY_KEY = "GET_MEDIA_OBJECT_QUERY_KEY";
const mediaAwaitStatuses = [
  TranscodeStatus.Processing,
  TranscodeStatus.Awaiting,
];

export function useGetMedia(options?: { isEdit?: boolean }): {
  isLoading: boolean;
  media: MediaItem[];
} {
  const { isEdit = false } = options ?? {};
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const { data: privateMediaData = [], isLoading: isMediaLoading } = useQuery<
    MediaMetadata[],
    AxiosError
  >(
    GET_MEDIA_QUERY_KEY,
    async () => {
      const { data } = await axios.get(
        getApiUrl(HALManager.getMediaUrl(UserManager.userId))
      );

      return data;
    },
    {
      enabled: !isPublicView,
      cacheTime: 0,
      onError: captureError,
    }
  );

  const mediaData = isPublicView
    ? publicProfileData?.mediaItems || []
    : privateMediaData;

  const externalMedia = _orderBy(
    (publicProfileData?.externalMediaItems ?? []).map((externalMedia) => {
      return {
        id: externalMedia.id,
        visible: true,
        sortOrder: externalMedia.sortOrder,
        mergedSortOrder: externalMedia.sortOrder,
        durationInMs: externalMedia.durationInMs,
        selectedThumbnail: 0,
        mediaInfoId: externalMedia.id,
        profileId: externalMedia.profileId,
        mediaType: externalMedia.mediaType,
        title: externalMedia.title,
        description: "",
        src: externalMedia.url,
        poster: externalMedia.thumbnail,
        isTranscodeError: false,
      };
    }),
    ["sortOrder"],
    ["asc"]
  );

  const mediaObjects = useQueries(
    mediaData.map((media) => {
      return {
        queryKey: [GET_MEDIA_OBJECT_QUERY_KEY, media.mediaInfoId],
        queryFn: async () => {
          try {
            const { data } = await axios.get(
              getTitaniumApiUrl(
                HALManager.getMediaObjectByIdUrl(media.mediaInfoId)
              )
            );

            return { media, ...data };
          } catch (e) {
            return {
              id: media.mediaInfoId,
              media,
              value: { transcodeStatus: TranscodeStatus.Error },
            };
          }
        },
        refetchInterval: (data) => {
          return mediaAwaitStatuses.includes(data?.value?.transcodeStatus)
            ? 2000
            : false;
        },
      };
    })
  );

  const mediaObjectLoading = mediaObjects.some(({ isLoading }) => isLoading);
  const isLoading =
    isMediaLoading || mediaObjectLoading || isPublicProfileLoading;
  const sortOrder = (mediaList) =>
    _orderBy(mediaList, ["mergedSortOrder"], ["asc"]);

  const media = isLoading
    ? []
    : sortOrder(
        mediaObjects
          .filter(({ data }) => {
            return data?.media?.visible;
          })
          .map(({ data }) => {
            const isProcessing = mediaAwaitStatuses.includes(
              data?.value?.transcodeStatus
            );
            const isTranscodeError =
              data?.value?.transcodeStatus === TranscodeStatus.Error;

            return {
              ...data.media,
              src:
                isProcessing || isTranscodeError
                  ? ""
                  : (data?.value?.outputFiles?.find(
                      ({ outputGroupType }) =>
                        outputGroupType === OutputGroupType.FILE
                    )?.objectUrls?.[0] ?? ""),
              poster: isProcessing
                ? ""
                : (data?.value?.thumbnails?.[
                    data.media?.selectedThumbnail || 0
                  ] ?? ""),
              isProcessing,
              isTranscodeError,
            };
          })
      );

  const allMedia = [...media, ...externalMedia];

  return {
    isLoading,
    media: isEdit
      ? allMedia
      : allMedia.filter((item) => !item.isTranscodeError),
  };
}
